import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormControl,
  FormErrorMessage,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { getAccountWebhookEventDescription, getPrettyAccountWebhookEvent } from '@pelicargo/utils';
import { AccountWebhookEvent } from '@prisma/client';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { TrpcOutputs } from '../../config/trpc';
import { useAccountWebhooks } from '../../hooks/accountWebhook/useAccountWebhooks';
import { SimpleForm } from '../SimpleForm';
import { SimpleInput } from '../SimpleInput';

type AccountWebhook = TrpcOutputs['getAccountWebhooksByAccount'][number];
type AccountWebhookActions = ReturnType<typeof useAccountWebhooks>;

const schema = z.object({
  uri: z
    .string()
    .trim()
    .min(1, 'Enter an endpoint URL')
    .url('Use an https:// URL')
    .refine((uri) => uri.startsWith('https://'), 'Use an https:// URL'),
  events: z.array(z.nativeEnum(AccountWebhookEvent)).length(1, 'Pick one event'),
});

type FormValues = z.infer<typeof schema>;

type Props = {
  accountId: number;
  webhook?: AccountWebhook;
  isOpen: boolean;
  onClose: () => void;
  createWebhook: AccountWebhookActions['createWebhook'];
  updateWebhook: AccountWebhookActions['updateWebhook'];
  isCreating: boolean;
  isUpdating: boolean;
};

const eventOptions = Object.values(AccountWebhookEvent);
const DEFAULT_VALUES: FormValues = { uri: '', events: [] };

export const AccountWebhookModal = ({
  accountId,
  webhook,
  isOpen,
  onClose,
  createWebhook,
  updateWebhook,
  isCreating,
  isUpdating,
}: Props) => {
  const methods = useForm<FormValues>({ defaultValues: DEFAULT_VALUES, resolver: zodResolver(schema) });
  const selectedEvents = methods.watch('events');
  const isEditing = !!webhook;

  useEffect(() => {
    if (!isOpen) return;
    methods.reset(webhook ? { uri: webhook.uri, events: [webhook.event] } : DEFAULT_VALUES);
  }, [isOpen, methods, webhook]);

  const handleClose = () => {
    methods.reset(DEFAULT_VALUES);
    onClose();
  };

  const handleEventChange = (event: AccountWebhookEvent, isChecked: boolean) => {
    methods.setValue('events', isChecked ? [event] : [], { shouldDirty: true, shouldValidate: true });
  };

  const handleSubmit = methods.handleSubmit(async (values) => {
    try {
      if (webhook) {
        await updateWebhook({ id: webhook.id, uri: values.uri, events: values.events });
      } else {
        await createWebhook({ account_id: accountId, uri: values.uri, events: values.events });
      }
      handleClose();
    } catch (error) {
      methods.setError('uri', {
        type: 'server',
        message: error?.message ?? 'The endpoint URL could not be saved',
      });
    }
  });

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <SimpleForm {...methods} submitOnBlur={false}>
          <ModalCloseButton />
          <ModalHeader>{isEditing ? 'Edit webhook' : 'Add webhook'}</ModalHeader>
          <ModalBody>
            <Stack spacing="4">
              <SimpleInput
                name="uri"
                label="Endpoint URL"
                placeholder="https://hooks.example.com/pelicargo"
                inputAttrs={{ fontFamily: 'monospace' }}
                isRequired
              />
              <FormControl isInvalid={!!methods.formState.errors.events}>
                <Text fontSize="xs" mb="2">
                  Events
                </Text>
                <Stack spacing="2">
                  {eventOptions.map((event) => (
                    <Box key={event} borderWidth="1px" borderRadius="md" p="3">
                      <Checkbox
                        isChecked={selectedEvents.includes(event)}
                        onChange={(changeEvent) => handleEventChange(event, changeEvent.target.checked)}
                      >
                        <Stack spacing="0">
                          <Text fontWeight="semibold">{getPrettyAccountWebhookEvent(event)}</Text>
                          <Text fontFamily="monospace" fontSize="xs">
                            {event}
                          </Text>
                          <Text color="gray.500" fontSize="sm">
                            {getAccountWebhookEventDescription(event)}
                          </Text>
                        </Stack>
                      </Checkbox>
                    </Box>
                  ))}
                </Stack>
                <FormErrorMessage>{methods.formState.errors.events?.message}</FormErrorMessage>
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
              <Button colorScheme="brand" onClick={handleSubmit} isLoading={isCreating || isUpdating}>
                {isEditing ? 'Save' : 'Add webhook'}
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </SimpleForm>
      </ModalContent>
    </Modal>
  );
};
